import React from 'react';
import { useNavigate, useParams } from 'react-router';
import { ArrowLeft } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import PageTitle from '../../headers/PageTitle';
import { formatDateTime } from '../../IAP/Resources/utils/dateUtils';
import { NotificationDeliveryReport } from '../../MapPage/CorporateViewTabs/NotificationsTab/NotificationDeliveryReport';
import { describeEscalationPlan } from '../../MapPage/CorporateViewTabs/NotificationsTab/escalationPlanModel';
import {
  SEND_STATUS_LABEL,
  NotificationSendStatus,
  sendStatusTone,
} from '../../MapPage/CorporateViewTabs/NotificationsTab/notificationSendsModel';
import {
  useCancelNotification,
  useOrgNotificationSend,
  type NotificationWithMetadataDto,
} from 'hooks/useNotification';

const TONE_VARIANT: Record<
  ReturnType<typeof sendStatusTone>,
  'green' | 'amber' | 'destructive' | 'outline'
> = {
  neutral: 'outline',
  good: 'green',
  warn: 'amber',
  bad: 'destructive',
};

/**
 * One send, in full: what was written on each channel, what ladder was
 * attached, and the per-recipient delivery report.
 *
 * Deep-linkable on purpose. "Why didn't I get the callout?" is a question that
 * arrives by phone hours later, and the answer needs to be a URL someone can
 * paste into a thread.
 */
const NotificationSendDetailPage: React.FC = () => {
  const navigate = useNavigate();
  const { notificationId } = useParams<{ notificationId: string }>();
  const { data, isLoading, isError } = useOrgNotificationSend(notificationId);
  const cancelSend = useCancelNotification();

  const notification = data?.notification;
  const title =
    notification?.metadata?.emailSubject ||
    notification?.metadata?.appMessage ||
    'Notification';

  return (
    <div className="p-4 pt-0">
      <PageTitle title={isLoading ? 'Loading…' : title} />

      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="tw:mb-3 tw:px-0"
        onClick={() => navigate('/notifications/sends')}
      >
        <ArrowLeft className="tw:h-4 tw:w-4" />
        All sends
      </Button>

      {isError ? (
        <p className="text-danger">
          This send could not be loaded. It may belong to another organization.
        </p>
      ) : (
        <div className="tw:space-y-4">
          {notification ? (
            <SendSummary
              notification={notification}
              onCancel={() =>
                void cancelSend.mutateAsync({ notificationId: notification.id })
              }
              cancelling={cancelSend.isPending}
            />
          ) : null}

          {notificationId ? (
            <NotificationDeliveryReport
              notificationId={notificationId}
              data={data?.report}
              isLoading={isLoading}
              isError={false}
            />
          ) : null}
        </div>
      )}
    </div>
  );
};

const CHANNEL_BODIES: {
  label: string;
  key: keyof NonNullable<NotificationWithMetadataDto['metadata']>;
}[] = [
  { label: 'In-app', key: 'appMessage' },
  { label: 'Email', key: 'emailMessage' },
  { label: 'SMS', key: 'smsMessage' },
  { label: 'Teams', key: 'teamsMessage' },
];

function SendSummary({
  notification,
  onCancel,
  cancelling,
}: {
  notification: NotificationWithMetadataDto;
  onCancel: () => void;
  cancelling: boolean;
}) {
  const status = notification.status as unknown as NotificationSendStatus;
  const ladder = describeEscalationPlan(notification.escalationPlan ?? null);
  const cancellable =
    status === NotificationSendStatus.SCHEDULED ||
    status === NotificationSendStatus.CREATED ||
    status === NotificationSendStatus.DRAFT;

  const when =
    notification.sentAt ?? notification.scheduledDate ?? notification.createdAt;

  return (
    <div className="tw:space-y-3 tw:rounded-md tw:border tw:border-border tw:p-3">
      <div className="tw:flex tw:flex-wrap tw:items-center tw:gap-2">
        <Badge variant={TONE_VARIANT[sendStatusTone(status)]}>
          {SEND_STATUS_LABEL[status] ?? notification.status}
        </Badge>
        <Badge variant="outline">{notification.priority}</Badge>
        {notification.acknowledgementRequired ? (
          <Badge variant="outline">Confirmation requested</Badge>
        ) : null}
        <span className="tw:text-xs tw:text-muted-foreground">
          {notification.sentAt
            ? `Sent ${formatDateTime(when)}`
            : notification.scheduledDate
              ? `Scheduled for ${formatDateTime(when)}`
              : `Created ${formatDateTime(when)}`}
        </span>
        {cancellable ? (
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="tw:ml-auto"
            disabled={cancelling}
            onClick={onCancel}
          >
            {cancelling ? 'Cancelling…' : 'Cancel send'}
          </Button>
        ) : null}
      </div>

      <div className="tw:grid tw:gap-3 tw:sm:grid-cols-2">
        {CHANNEL_BODIES.map(({ label, key }) => {
          const body = notification.metadata?.[key];
          if (typeof body !== 'string' || !body) return null;

          return (
            <div key={label} className="tw:space-y-0.5">
              <p className="tw:m-0 tw:text-[11px] tw:font-medium tw:text-muted-foreground">
                {label}
              </p>
              {key === 'emailMessage' && notification.metadata?.emailSubject ? (
                <p className="tw:m-0 tw:text-xs tw:font-medium">
                  {notification.metadata.emailSubject}
                </p>
              ) : null}
              <p className="tw:m-0 tw:text-xs tw:whitespace-pre-wrap">{body}</p>
            </div>
          );
        })}
      </div>

      {ladder.length > 0 ? (
        <div className="tw:space-y-0.5">
          <p className="tw:m-0 tw:text-[11px] tw:font-medium tw:text-muted-foreground">
            Escalation
          </p>
          <ol className="tw:m-0 tw:list-decimal tw:pl-4 tw:text-xs tw:text-muted-foreground">
            {ladder.map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ol>
        </div>
      ) : null}
    </div>
  );
}

export default NotificationSendDetailPage;
